import { darken, lighten, rgba } from 'polished';
import { CSSProperties } from 'styled-components';

const COLORS = {
  primary: '#587DFF',
  blue: '#3656FB',
  jetblack: '#112233',
  black: '#223344',
  grey: '#445566',
  green: '#30BA88',
  lightgrey: '#778899',
  white: '#FFFFFF',
  pink: '#EB507F',
  purple: '#C57CD1',
  yellow: '#FFC700',
  brown: '#462E00',
  royalblue: '#3656FB',
} as const;

export const appTheme = {
  screen: {
    min: (styles: { [K in keyof CSSProperties]: CSSProperties[K] }) => {
      return {
        '@media screen and (max-width: 640px)': styles,
      };
    },
    sp: (styles: { [K in keyof CSSProperties]: CSSProperties[K] }) => {
      return {
        '@media screen and (max-width: 799px)': styles,
      };
    },
    tablet: (styles: { [K in keyof CSSProperties]: CSSProperties[K] }) => {
      return {
        '@media screen and (max-width: 1024px)': styles,
      };
    },
    pc: (styles: { [K in keyof CSSProperties]: CSSProperties[K] }) => {
      return {
        '@media screen and (min-width: 800px)': styles,
      };
    },
  },

  backgroundColors: {
    default: '#F7F9FB',
    primary: COLORS.primary,
    secondary: COLORS.pink,
    white: COLORS.white,
    grey: COLORS.grey,
    lightgrey: COLORS.lightgrey,
    black: COLORS.black,
    jetblack: COLORS.jetblack,
    green: COLORS.green,
    purple: COLORS.purple,
    blue: COLORS.primary,
    royalblue: COLORS.royalblue,
    yellow: COLORS.yellow,
    transparent: 'transparent',
    error: lighten(0.32, COLORS.pink),
    disabled: rgba(COLORS.lightgrey, 0.4),
    linearGreen: `linear-gradient(180deg, ${lighten(0.06, COLORS.green)} 0%, ${
      COLORS.green
    } 100%)`,
    linearBlue: `linear-gradient(180deg, ${lighten(0.06, COLORS.primary)} 0%, ${
      COLORS.primary
    } 100%)`,
  },

  borderColors: {
    default: '#DAE4EE',
    primary: COLORS.primary,
    secondary: COLORS.pink,
    white: COLORS.white,
    lightgrey: COLORS.lightgrey,
    green: COLORS.green,
    purple: COLORS.purple,
    black: COLORS.black,
    jetblack: COLORS.jetblack,
    yellow: COLORS.yellow,
    transparent: 'transparent',
    error: COLORS.pink,
  },

  fontFamilys: {
    jp: `YakuHanJPs, -apple-system,BlinkMacSystemFont,Helvetica Neue,Segoe UI,Hiragino Kaku Gothic ProN,Hiragino Sans,ヒラギノ角ゴ ProN W3,Arial,メイリオ,Meiryo,sans-serif`,
  },

  fontSizes: {
    // from https://min-max-calculator.9elements.com/
    xxxs: 'clamp(0.6rem, 0.527rem + 0.36vw, 0.8rem)',
    xxs: 'clamp(0.8rem, 0.727rem + 0.36vw, 1rem)',
    xs: 'clamp(1rem, 0.927rem + 0.36vw, 1.2rem)',
    s: 'clamp(1.2rem, 1.127rem + 0.36vw, 1.4rem)',
    m: 'clamp(1.4rem, 1.327rem + 0.36vw, 1.6rem)',
    l: 'clamp(1.6rem, 1.527rem + 0.36vw, 1.8rem)',
    xl: 'clamp(1.8rem, 1.727rem + 0.36vw, 2rem)',
    xxl: 'clamp(2rem, 1.855rem + 0.73vw, 2.4rem)',
    xxxl: 'clamp(2.4rem, 2.109rem + 1.45vw, 3.2rem)',
    xxxxl: 'clamp(3.2rem, 2.909rem + 1.45vw, 4rem)',
    xxxxxl: 'clamp(4rem, 3.709rem + 1.45vw, 4.8rem)',
    ex: 'clamp(4.8rem, 2.873rem + 3.64vw, 5.6rem)',
  },

  fontColors: {
    default: COLORS.black,
    white: COLORS.white,
    primary: COLORS.primary,
    grey: COLORS.grey,
    lightgrey: COLORS.lightgrey,
    green: COLORS.green,
    purple: COLORS.purple,
    pink: COLORS.pink,
    blue: COLORS.blue,
    yellow: COLORS.yellow,
    placeholder: '#aabbcc',
    brown: COLORS.brown,
  },

  fontWeights: {
    light: 300,
    normal: 400,
    bold: 700,
  },

  lineHeights: {
    s: 1,
    default: 1.4,
    m: 1.6,
    l: 2,
    xl: 2.5,
  },

  spaces: {
    xs: '2px',
    s: '4px',
    m: '8px',
    l: '16px',
    xl: '24px',
    xxl: '32px',
    xxxl: '40px',
    xxxxl: '64px',
    ex: '80px',
  },

  transitions: {
    fast: '0.1s',
    normal: '0.2s',
    slow: '0.5s',
  },

  windowSizes: {
    s: '640px',
    m: '1080px',
    l: '1200px',
    xl: '1440px',
  },

  radii: {
    s: '4px',
    m: '8px',
    l: '16px',
    rounded: '9999px',
    circle: '50%',
  },

  boxShadow: {
    hover: `0 2px -14px ${rgba(COLORS.primary, 0.5)}, 4px -16px -24px ${rgba(
      COLORS.primary,
      0.3,
    )}`,
    hover2: `0 2px 14px ${rgba(COLORS.primary, 0.5)}, 4px 16px 32px ${rgba(
      COLORS.primary,
      0.3,
    )}`,
    nav: `0 1px 1px ${rgba(COLORS.primary, 0.1)}, 4px 16px 32px ${rgba(
      COLORS.black,
      0.3,
    )}`,
    base: `0 16px 80px ${rgba(COLORS.primary, 0.1)}, 0px 2px 20px 0px ${rgba(
      COLORS.primary,
      0.2,
    )}`,
    blue: `0 16px 10px ${rgba(COLORS.primary, 0.1)}, 0px 2px 10px 0px ${rgba(
      COLORS.primary,
      0.5,
    )}`,
    pink: `0 16px 10px ${rgba(COLORS.pink, 0.1)}, 0px 2px 10px 0px ${rgba(
      COLORS.pink,
      0.3,
    )}`,
    green: `0 16px 10px ${rgba(COLORS.green, 0.1)}, 0px 2px 10px 0px ${rgba(
      COLORS.green,
      0.3,
    )}`,
    purple: `0 16px 10px ${rgba(COLORS.purple, 0.1)}, 0px 2px 10px 0px ${rgba(
      COLORS.purple,
      0.3,
    )}`,
    yellow: `0 16px 10px ${rgba(COLORS.yellow, 0.1)}, 0px 2px 10px 0px ${rgba(
      COLORS.yellow,
      0.3,
    )}`,
  },
} as const;

export type AppTheme = typeof appTheme;

declare module 'styled-components' {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface DefaultTheme extends AppTheme {}
}
